import * as pdfjsLib from 'pdfjs-dist';

// Configure worker for Vite/browser environment
pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.mjs',
  import.meta.url
).toString();

/**
 * Extract plain text from a PDF File object.
 * Returns all pages concatenated with newlines between them.
 * Returns empty string if extraction fails (upload still proceeds).
 */
export async function extractPdfText(file: File): Promise<string> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
    const pdf = await loadingTask.promise;

    const pageTexts: string[] = [];

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();

      // Group items by y-position to reconstruct lines
      const lines: Map<number, string[]> = new Map();
      for (const item of textContent.items) {
        if ('str' in item && item.str.trim()) {
          const y = Math.round((item as any).transform?.[5] ?? 0);
          if (!lines.has(y)) lines.set(y, []);
          lines.get(y)!.push(item.str);
        }
      }

      // Sort lines top-to-bottom (descending y in PDF coords)
      const sortedY = [...lines.keys()].sort((a, b) => b - a);
      const pageText = sortedY
        .map(y => lines.get(y)!.join(' '))
        .join('\n');

      if (pageText.trim()) {
        pageTexts.push(pageText.trim());
      }
    }

    return pageTexts.join('\n\n');
  } catch (err) {
    console.warn('[pdf-extract] Failed to extract text:', err);
    return '';
  }
}

/**
 * Extract plain text from a plain-text file (txt, md, etc.)
 */
export async function extractTextFileContent(file: File): Promise<string> {
  try {
    return await file.text();
  } catch {
    return '';
  }
}

/**
 * Extract text from any supported file type.
 */
export async function extractDocumentText(file: File): Promise<string> {
  if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
    return extractPdfText(file);
  }
  if (
    file.type === 'text/plain' ||
    file.name.toLowerCase().endsWith('.txt') ||
    file.name.toLowerCase().endsWith('.md')
  ) {
    return extractTextFileContent(file);
  }
  // For .doc/.docx we can't extract client-side without a library — return empty
  return '';
}
